<template>
  <div class="font-sans bg-[#f8f9fa] min-h-screen text-[#191c1d]">

    <!-- Hero Section -->
    <section class="relative py-20 pb-24 overflow-hidden">
      <div class="absolute inset-0 pointer-events-none">
        <div class="absolute inset-0 bg-[#f8f9fa]"></div>
        <svg class="absolute top-0 right-0 w-2/3 h-full translate-x-1/4 -translate-y-1/4" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,1000 C300,800 400,1000 700,800 C1000,600 1000,0 1000,0 L0,0 Z" fill="#071c5b" opacity="0.2"/>
        </svg>
        <svg class="absolute bottom-0 left-0 w-full h-2/3" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,1000 C200,900 400,1000 600,800 C800,600 1000,700 1000,500 L1000,1000 Z" fill="#006e29" opacity="0.15"/>
        </svg>
        <div class="absolute top-10 left-10 w-64 h-64 rounded-full bg-[#f5c343] opacity-20 blur-[100px]"></div>
        <div class="absolute bottom-20 right-20 w-80 h-80 rounded-full bg-[#d24340] opacity-[0.12] blur-[100px]"></div>
      </div>

      <div class="relative z-10 max-w-7xl mx-auto px-6 text-center">
        <h1 class="text-[clamp(2rem,5vw,3rem)] font-bold text-[#071c5b] tracking-tight leading-tight mb-4">
          Wie wil je vandaag helpen?
        </h1>
        <p class="text-lg leading-relaxed text-[#454650] max-w-2xl mx-auto mb-12">
          Samen bouwen we aan een sterker zorgnetwerk. Vind direct de juiste ondersteuning voor jouw patiënten.
        </p>
        <div class="max-w-3xl mx-auto relative group">
          <div
            class="absolute -inset-1 bg-gradient-to-r from-[#7df98f] to-[#dde1ff] rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-300 pointer-events-none"></div>

          <!-- Prompt input -->
          <div
            v-if="!chatOpen"
            class="relative tw-flex items-center bg-white/90 backdrop-blur-sm border border-[#c6c5d2] rounded-2xl p-2 shadow-sm cursor-pointer"
            @click="openChat"
          >
            <span class="flex-1 px-4 py-3 text-base text-[#757681] text-left select-none">
              Stel een vraag over zorg en welzijn...
            </span>
            <button class="bg-[#071c5b] text-white px-8 py-3 rounded-xl text-sm font-semibold whitespace-nowrap flex-shrink-0 hover:opacity-90 transition-opacity">
              Start gesprek
            </button>
          </div>

          <!-- Chatbot iframe — expands when open -->
          <div name="chat-expand">
            <div v-if="chatOpen" ref="chatContainer" class="relative rounded-2xl overflow-hidden border border-[#c6c5d2] shadow-sm bg-white">
              <button
                class="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm border border-[#c6c5d2] tw-flex items-center justify-center hover:bg-white transition-colors shadow-sm"
                @click="chatOpen = false"
              >
                <span class="material-symbols-outlined text-[#071c5b] text-base">close</span>
              </button>
              <iframe
                src="https://zorgenwelzijn.datascouts.eu/chatbot/1/embed?apiKey=ds_chat_stc6ZcBGHu4RZdmzoJiS"
                width="100%"
                height="700"
                frameborder="0"
                allow="clipboard-write"
                style="display: block;"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Quick Actions -->
    <section class="-mt-12 relative z-20 pb-12">
      <div class="max-w-7xl mx-auto px-6">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div
            v-for="action in quickActions"
            :key="action.key"
            class="bg-white rounded-xl shadow-sm border-t-4 p-6 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all group"
            :style="{ borderTopColor: action.color }"
            @click="action.key === 'patient' ? openChat() : navigateTo(action.route)"
          >
            <div
              class="w-12 h-12 rounded-lg tw-flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
              :style="{ background: action.bgColor }"
            >
              <span class="material-symbols-outlined text-2xl" :style="{ color: action.iconColor }">{{ action.icon }}</span>
            </div>
            <h3 class="text-base font-semibold text-[#191c1d] mb-1">{{ action.title }}</h3>
            <p class="text-sm text-slate-500 leading-snug">{{ action.subtitle }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Care Providers Near You -->
    <section class="py-16">
      <div class="max-w-7xl mx-auto px-6">
        <div class="tw-flex items-center justify-between mb-6">
          <h2 class="text-3xl font-bold text-[#071c5b] leading-tight">Zorgverleners nabij</h2>
          <span class="tw-flex items-center gap-2 bg-[#edeeef] text-[#454650] text-xs font-semibold px-4 py-1.5 rounded-full">
            <span class="w-2 h-2 rounded-full bg-[#006e29] flex-shrink-0"></span>
            {{ careProviders.length }} zorgverleners in de buurt
          </span>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:h-[600px]">
          <!-- Provider List -->
          <div class="lg:col-span-5 tw-flex flex-col gap-4 overflow-y-auto pr-2 h-[320px] lg:h-auto">
            <!-- Skeletons -->
            <template v-if="careProvidersLoading">
              <div v-for="n in 4" :key="n" class="bg-white p-4 rounded-xl border border-[#c6c5d2] tw-flex gap-4 pointer-events-none flex-shrink-0">
                <div class="w-16 h-16 rounded-lg flex-shrink-0 shimmer"></div>
                <div class="tw-flex flex-col flex-1 gap-2">
                  <div class="tw-flex justify-between gap-2">
                    <div class="h-4 w-3/5 rounded shimmer"></div>
                    <div class="h-4 w-[72px] rounded-full flex-shrink-0 shimmer"></div>
                  </div>
                  <div class="h-3 w-2/5 rounded shimmer"></div>
                  <div class="h-3 w-5/6 rounded shimmer"></div>
                  <div class="tw-flex gap-2">
                    <div class="h-5 w-16 rounded shimmer"></div>
                    <div class="h-5 w-16 rounded shimmer"></div>
                  </div>
                </div>
              </div>
            </template>
            <!-- Real cards -->
            <template v-else>
              <div
                v-for="provider in careProviders"
                :key="provider.id"
                class="bg-white p-4 rounded-xl border border-[#c6c5d2] tw-flex gap-4 cursor-pointer flex-shrink-0 hover:border-[#071c5b] transition-colors"
                @click="navigateTo('/actors/' + provider.elasticId)"
              >
                <div class="w-16 h-16 rounded-lg bg-[#dde1ff] tw-flex items-center justify-center flex-shrink-0">
                  <span class="material-symbols-outlined text-[28px] text-[#071c5b]">{{ provider.icon }}</span>
                </div>
                <div class="tw-flex flex-col flex-1 min-w-0">
                  <div class="tw-flex justify-between items-start gap-2">
                    <h4 class="text-base font-semibold text-[#191c1d] leading-snug">{{ provider.name }}</h4>
                    <span
                      class="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
                      :class="provider.available ? 'bg-[#7df98f] text-[#00732b]' : 'bg-[#ffdf9a] text-[#251a00]'"
                    >
                      {{ provider.available ? 'Beschikbaar' : provider.waitlist }}
                    </span>
                  </div>
                  <p class="text-xs text-slate-500 mt-0.5">
                    {{ provider.role }}
                    <template v-if="provider.distance"> • {{ provider.distance }} km afstand</template>
                  </p>
                  <p v-if="provider.description" class="text-xs text-[#454650] leading-snug mt-1 line-clamp-2">{{ provider.description }}</p>
                  <div class="tw-flex flex-wrap gap-2 mt-2">
                    <span v-for="tag in provider.tags" :key="tag" class="text-[10px] bg-[#edeeef] text-[#454650] px-2 py-0.5 rounded whitespace-nowrap">{{ tag }}</span>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <!-- Map -->
          <div class="lg:col-span-7 relative rounded-xl overflow-hidden shadow-sm border border-[#c6c5d2] h-[400px] lg:h-auto">
            <iframe
              :src="'https://zorgenwelzijn.datascouts.eu/ecosystem-maps/5/embed?apiKey=ds_live_hWtlEHdZ4MGNBRmLTIpE&language=' + language"
              width="100%"
              height="100%"
              frameborder="0"
              allowfullscreen
              style="border: none;"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Smart Suggestions -->
    <section class="bg-[#f3f4f5] pt-10 pb-16">
      <div class="max-w-7xl mx-auto px-6">
        <h2 class="text-3xl font-bold text-[#071c5b] leading-tight mb-12">Slimme suggesties voor jou</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <!-- Wide bento card: Events -->
          <div class="suggestions-card md:col-span-2 bg-white rounded-3xl p-6 shadow-sm border border-[#c6c5d2] overflow-hidden tw-flex flex-col gap-4">
            <span class="inline-block px-3 py-1 bg-[#dde1ff] text-[#324381] rounded-full text-sm font-semibold self-start">Aankomende evenementen</span>

            <!-- Loading skeletons -->
            <template v-if="eventsLoading">
              <div v-for="n in 3" :key="n" class="tw-flex gap-4 items-start py-3 border-b border-[#f0f0f0] last:border-0">
                <div class="w-10 h-10 rounded-lg flex-shrink-0 shimmer"></div>
                <div class="tw-flex flex-col flex-1 gap-2">
                  <div class="h-4 w-3/4 rounded shimmer"></div>
                  <div class="h-3 w-1/3 rounded shimmer"></div>
                </div>
              </div>
            </template>

            <!-- Events list -->
            <TransitionGroup v-else-if="events.length" name="stagger" tag="div" class="tw-flex flex-col">
              <div
                v-for="(event, i) in events"
                :key="event.id"
                class="event-row tw-flex gap-4 items-start py-3 border-b border-[#f0f0f0] last:border-0 cursor-pointer group rounded-xl px-2 -mx-2"
                :style="{ '--i': i }"
                @click="navigateTo('/events/' + (event.slug || event.id))"
              >
                <div class="event-icon w-10 h-10 rounded-lg bg-[#dde1ff] tw-flex items-center justify-center flex-shrink-0 transition-transform duration-200">
                  <span class="material-symbols-outlined text-[20px] text-[#071c5b]">event</span>
                </div>
                <div class="tw-flex flex-col flex-1 min-w-0">
                  <p class="text-sm font-semibold text-[#191c1d] leading-snug group-hover:text-[#071c5b] transition-colors duration-150">{{ event.title }}</p>
                  <p v-if="event.start_date" class="text-xs text-slate-400 mt-0.5">{{ formatEventDate(event.start_date) }}</p>
                  <p v-if="event.description" class="text-xs text-[#454650] mt-1 line-clamp-1">{{ event.description }}</p>
                </div>
                <span
                  class="material-symbols-outlined text-[#c6c5d2] text-base self-center opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-150">chevron_right</span>
              </div>
            </TransitionGroup>

            <p v-else class="text-sm text-slate-400 py-4">Geen aankomende evenementen gevonden.</p>

            <button
              class="tw-flex items-center gap-2 font-bold text-[0.9rem] text-[#071c5b] bg-transparent border-none cursor-pointer p-0 w-fit hover:gap-4 transition-all duration-200 mt-auto"
              @click="navigateTo('/events')"
            >
              Alle evenementen
              <span class="material-symbols-outlined">arrow_forward</span>
            </button>
          </div>

          <!-- Dark quick access card: KB posts -->
          <div class="suggestions-card bg-[#071c5b] rounded-3xl p-6 tw-flex flex-col justify-between">
            <div>
              <span class="material-symbols-outlined text-[40px] text-[#f1c040] block kb-icon">menu_book</span>
              <h3 class="text-2xl font-bold text-white leading-tight mt-4 mb-2">Kennisbank</h3>
              <p class="text-sm text-blue-200/80 leading-relaxed">Recente artikelen en richtlijnen voor jouw praktijk.</p>
            </div>
            <TransitionGroup name="stagger" tag="ul" class="list-none p-0 mt-6 tw-flex flex-col gap-3">
              <template v-if="kbLoading">
                <li v-for="n in 4" :key="'sk-' + n" class="h-10 rounded-xl shimmer-dark"></li>
              </template>
              <template v-else-if="kbPosts.length">
                <li
                  v-for="(post, i) in kbPosts"
                  :key="post.id"
                  class="kb-row tw-flex items-center gap-3 bg-white/10 px-3 py-3 rounded-xl cursor-pointer text-white text-sm font-medium"
                  :style="{ '--i': i }"
                  @click="navigateTo('/knowledge-base/posts/' + post.id)"
                >
                  <span class="material-symbols-outlined flex-shrink-0 transition-transform duration-200">description</span>
                  <span class="line-clamp-1 flex-1">{{ post.title }}</span>
                  <span class="material-symbols-outlined text-sm opacity-0 kb-arrow transition-all duration-150">chevron_right</span>
                </li>
              </template>
              <li v-else key="empty" class="text-blue-200/60 text-sm px-3 py-2">Geen artikelen gevonden.</li>
            </TransitionGroup>
          </div>
        </div>
      </div>
    </section>

    <!-- New Actors in Network -->
    <section class="py-16">
      <div class="max-w-7xl mx-auto px-6">
        <div class="tw-flex items-center justify-between mb-12">
          <div class="tw-flex items-center gap-4">
            <div class="p-3 bg-[#7df98f] rounded-2xl tw-flex items-center justify-center">
              <span class="material-symbols-outlined text-[#00732b]">dynamic_feed</span>
            </div>
            <h2 class="text-3xl font-bold text-[#071c5b] leading-tight">Nieuw in jouw netwerk</h2>
          </div>
          <button
            class="tw-flex items-center gap-2 font-bold text-[0.9rem] text-[#071c5b] bg-transparent border-none cursor-pointer p-0 hover:gap-4 transition-all duration-200"
            @click="navigateTo('/actors-simplified')"
          >
            Bekijk alle
            <span class="material-symbols-outlined">arrow_forward</span>
          </button>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <!-- Loading skeletons -->
          <template v-if="newActorsLoading">
            <div v-for="n in 6" :key="n" class="bg-white p-6 rounded-2xl border border-[#c6c5d2]">
              <div class="tw-flex items-center gap-3 mb-4">
                <div class="w-12 h-12 rounded-xl flex-shrink-0 shimmer"></div>
                <div class="tw-flex flex-col flex-1 gap-2">
                  <div class="h-4 w-3/4 rounded shimmer"></div>
                  <div class="h-3 w-1/2 rounded shimmer"></div>
                </div>
              </div>
              <div class="h-3 w-full rounded shimmer mb-2"></div>
              <div class="h-3 w-4/5 rounded shimmer mb-6"></div>
              <div class="h-8 w-32 rounded-lg shimmer"></div>
            </div>
          </template>

          <!-- Actor cards -->
          <template v-else-if="newActors.length">
            <div
              v-for="(actor, i) in newActors"
              :key="actor.id"
              class="actor-card bg-white p-6 rounded-2xl border border-[#c6c5d2] tw-flex flex-col gap-3 cursor-pointer hover:border-[#071c5b] hover:shadow-md transition-all duration-200"
              :style="{ '--i': i }"
              @click="navigateTo('/actors/' + actor.id)"
            >
              <!-- Header -->
              <div class="tw-flex items-center gap-3">
                <div class="w-12 h-12 rounded-xl bg-[#dde1ff] tw-flex items-center justify-center flex-shrink-0 overflow-hidden">
                  <img v-if="actor.logo" :src="actor.logo" :alt="actor.name" class="w-full h-full object-cover"/>
                  <span v-else class="material-symbols-outlined text-[#071c5b] text-2xl">domain</span>
                </div>
                <div class="tw-flex flex-col min-w-0">
                  <p class="text-sm font-bold text-[#191c1d] m-0 leading-snug line-clamp-1">{{ actor.name }}</p>
                  <!--                  <p v-if="actor.category" class="text-xs text-slate-400 m-0 mt-0.5 line-clamp-1">{{ actor.category }}</p>-->
                </div>
              </div>
              <!-- Description -->
              <p
                v-if="actor.description || actor.activities_description"
                class="text-xs text-[#454650] leading-relaxed line-clamp-2 m-0 flex-1"
              >{{ stripHtmlTags(actor.description || actor.activities_description) }}</p>
              <!-- Footer -->
              <div class="tw-flex items-center justify-between mt-auto pt-1">
                <span class="text-[10px] bg-[#edeeef] text-[#454650] px-2 py-0.5 rounded font-semibold">
                  Zorgverlener
                </span>
                <!-- Already in network -->
                <button
                  v-if="isInNetwork(actor.id)"
                  class="tw-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#7df98f] text-[#00732b] border-none cursor-default"
                  disabled
                  @click.stop
                >
                  <span class="material-symbols-outlined text-[16px]">check</span>
                  In netwerk
                </button>
                <!-- Add button -->
                <button
                  v-else
                  class="tw-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all duration-150"
                  :class="addingToNetwork[actor.id]
                    ? 'bg-[#f3f4f5] text-slate-400 border-[#e0e0e0] cursor-wait'
                    : 'bg-white text-[#071c5b] border-[#071c5b] hover:bg-[#071c5b] hover:text-white cursor-pointer'"
                  :disabled="addingToNetwork[actor.id]"
                  @click.stop="addToNetwork(actor)"
                >
                  <span class="material-symbols-outlined text-[16px]">{{ addingToNetwork[actor.id] ? 'hourglass_empty' : 'person_add' }}</span>
                  {{ addingToNetwork[actor.id] ? 'Bezig...' : 'Toevoegen' }}
                </button>
              </div>
            </div>
          </template>

          <!-- Empty state -->
          <p v-else class="col-span-3 text-sm text-slate-400 text-center py-12">
            Geen nieuwe zorgverleners gevonden.
          </p>
        </div>
      </div>
    </section>

    <SimplifiedFooterSamergo/>

    <!-- FAB -->
    <button
      class="fixed bottom-8 right-8 w-16 h-16 bg-[#071c5b] text-white border-none rounded-full shadow-[0_8px_24px_rgba(7,28,91,0.35)] tw-flex items-center justify-center z-50 cursor-pointer hover:scale-105 active:scale-95 transition-transform"
      @click="navigateTo('/actors-simplified')"
    >
      <span class="material-symbols-outlined text-[28px]">add</span>
    </button>

  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import SimplifiedFooterSamergo from '@/components/SimplifiedFooter/SimplifiedFooterSamergo.vue'
  import TranslationsMixin from '@/util/TranslationsMixin'
  import UiMixin from '@/util/UiMixin'
  import { fetchPublicNotifications } from '@/api/notifications'
  import { fetchPosts as fetchKbPostsApi } from '@/Domain/KnowledgeBase/Api/knowledgeBase'
  import { fetchLatestActors } from '@/api/homepage'
  import { ACTION_TYPES as NETWORK_ACTION_TYPES } from '@/store/modules/network-contacts'
  import { stripHtmlTags } from '@/util/helpers'

  const MAP_API_KEY = 'ds_live_hWtlEHdZ4MGNBRmLTIpE'
  const MAP_API_BASE = 'https://zorgenwelzijn.datascouts.eu/api/ecosystem-maps/actors'

  export default defineComponent({
    name: 'StaticHomeSamergoNew',
    mixins: [TranslationsMixin, UiMixin],
    components: {
      SimplifiedFooterSamergo,
    },
    data() {
      return {
        searchQuery: '', // kept for potential future use
        chatOpen: false,
        careProvidersLoading: true,
        quickActions: [
          {
            key: 'patient',
            icon: 'person_search',
            title: 'Hulp vinden voor een patiënt',
            subtitle: 'Vind direct gespecialiseerde zorgtrajecten.',
            color: '#071c5b',
            bgColor: '#dde1ff',
            iconColor: '#071c5b',
            route: '/actors-simplified',
          },
          {
            key: 'professional',
            icon: 'corporate_fare',
            title: 'Zoek een professional',
            subtitle: 'Contacteer collega\'s in jouw vakgebied.',
            color: '#7df98f',
            bgColor: '#7df98f',
            iconColor: '#00732b',
            route: '/actors-simplified',
          },
          {
            key: 'regional',
            icon: 'map',
            title: 'Regionale diensten',
            subtitle: 'Verken het lokale aanbod in de buurt.',
            color: '#f1c040',
            bgColor: '#ffdf9a',
            iconColor: '#251a00',
            route: '/actors-simplified',
          },
          {
            key: 'network',
            icon: 'hub',
            title: 'Mijn netwerk',
            subtitle: 'Beheer je vaste samenwerkingen.',
            color: '#ffdad6',
            bgColor: '#ffdad6',
            iconColor: '#ba1a1a',
            route: '/my-network',
          },
        ],
        careProviders: [],
        events: [],
        eventsLoading: true,
        kbPosts: [],
        kbLoading: true,
        newActors: [],
        newActorsLoading: true,
        addingToNetwork: {} as Record<string, boolean>,
      }
    },
    computed: {
      language() {
        return this.$store.getters.activeLocale
      },
    },
    async mounted() {
      this.$store.dispatch(NETWORK_ACTION_TYPES.FETCH)
      this.fetchCareProviders()
      this.fetchEvents()
      this.fetchKbPosts()
      this.fetchNewActors()
      window.addEventListener('message', this.onMapMessage)
    },
    beforeUnmount() {
      window.removeEventListener('message', this.onMapMessage)
    },
    methods: {
      stripHtmlTags,
      navigateTo(route: string) {
        this.$router.push(route)
      },
      async openChat() {
        this.chatOpen = true
        await this.$nextTick()
        const el = this.$refs.chatContainer as HTMLElement
        if (!el) return
        const scrollEl = el.closest('.main-view') as HTMLElement
        if (scrollEl) {
          const elRect = el.getBoundingClientRect()
          const containerRect = scrollEl.getBoundingClientRect()
          scrollEl.scrollTop += elRect.top - containerRect.top - 20
        }
      },
      async fetchEvents() {
        this.eventsLoading = true
        try {
          const res = await fetchPublicNotifications(5, 0, { is_event: [1] })
          this.events = Array.isArray(res) ? res : (res?.data ?? res?.results ?? [])
        } catch {
          // keep empty
        } finally {
          this.eventsLoading = false
        }
      },
      async fetchKbPosts() {
        this.kbLoading = true
        try {
          const res = await fetchKbPostsApi({ per_page: 5 })
          console.log(res)
          this.kbPosts = Array.isArray(res?.data.data.posts) ? res.data.data.posts : []
        } catch {
          // keep empty
        } finally {
          this.kbLoading = false
        }
      },
      async fetchNewActors() {
        this.newActorsLoading = true
        try {
          const actors = await fetchLatestActors({ limit: 6 })
          this.newActors = Array.isArray(actors) ? actors : []
        } catch {
          // keep empty
        } finally {
          this.newActorsLoading = false
        }
      },
      isInNetwork(actorId: string) {
        return this.$store.getters['isNetworkContact'](actorId)
      },
      async addToNetwork(actor: any) {
        if (this.addingToNetwork[actor.id] || this.isInNetwork(actor.id)) return
        this.addingToNetwork = { ...this.addingToNetwork, [actor.id]: true }
        try {
          await this.$store.dispatch(NETWORK_ACTION_TYPES.ADD_CONTACT, {
            elasticId: actor.id,
            name: actor.name,
            role: actor.category ?? '',
            image: actor.logo ?? null,
          })
        } catch {
          // silently fail
        } finally {
          this.addingToNetwork = { ...this.addingToNetwork, [actor.id]: false }
        }
      },
      formatEventDate(dateStr: string) {
        if (!dateStr) return ''
        const d = new Date(dateStr)
        return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
      },
      async fetchCareProviders(bounds?: { tl: number[], br: number[] }) {
        this.careProvidersLoading = true
        try {
          let url = `${MAP_API_BASE}?apiKey=${MAP_API_KEY}&limit=100`
          if (bounds) {
            url += `&tl[]=${bounds.tl[0]}&tl[]=${bounds.tl[1]}&br[]=${bounds.br[0]}&br[]=${bounds.br[1]}`
          }
          const res = await fetch(url)
          if (!res.ok) return
          const data = await res.json()
          const actors = Array.isArray(data) ? data : (data.data ?? [])
          this.careProviders = actors.map((actor: any) => ({
            id: actor.id,
            elasticId: actor.elastic_id,
            icon: 'domain',
            name: actor.name,
            role: actor.category?.name ?? actor.category ?? '',
            description: actor.description ?? '',
            distance: '',
            available: true,
            waitlist: null,
            tags: [
              ...(actor.technology ?? []).slice(0, 2),
              ...(actor.industries ?? []).slice(0, 1),
            ],
          }))
        } catch {
          // silently keep whatever was previously loaded
        } finally {
          this.careProvidersLoading = false
        }
      },
      onMapMessage(event: MessageEvent) {
        if (!String(event.origin).includes('datascouts')) return
        if (event.data?.type !== 'ds:mapBoundsChanged') return
        this.fetchCareProviders(event.data.bounds)
      },
    },
  })
</script>

<style scoped>
  .chat-expand-enter-active,
  .chat-expand-leave-active {
    transition: max-height 0.45s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
    overflow: hidden;
    max-height: 730px;
  }

  .chat-expand-enter-from,
  .chat-expand-leave-to {
    max-height: 0;
    opacity: 0;
  }

  @keyframes shimmer {
    0% {
      background-position: -400px 0;
    }
    100% {
      background-position: 400px 0;
    }
  }

  .shimmer {
    background: linear-gradient(90deg, #e8e9ec 25%, #f3f4f5 50%, #e8e9ec 75%);
    background-size: 800px 100%;
    animation: shimmer 1.4s infinite linear;
  }

  .shimmer-dark {
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.08) 25%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0.08) 75%);
    background-size: 800px 100%;
    animation: shimmer 1.4s infinite linear;
  }

  /* Card entrance */
  .suggestions-card {
    animation: fade-up 0.5s ease both;
  }

  .suggestions-card:nth-child(2) {
    animation-delay: 0.1s;
  }

  /* Staggered list entrance */
  .stagger-enter-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
    transition-delay: calc(var(--i, 0) * 60ms);
  }

  .stagger-enter-from {
    opacity: 0;
    transform: translateY(8px);
  }

  /* Event row hover */
  .event-row:hover {
    background: #f8f9ff;
  }

  .event-row:hover .event-icon {
    transform: scale(1.1) rotate(-4deg);
    background: #c8ceff;
  }

  /* KB row hover */
  .kb-row {
    transition: background 0.15s ease, transform 0.15s ease;
  }

  .kb-row:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: translateX(3px);
  }

  .kb-row:hover .kb-arrow {
    opacity: 1;
  }

  .kb-row:active {
    transform: translateX(3px) scale(0.98);
  }

  /* KB icon float */
  .kb-icon {
    animation: float 3s ease-in-out infinite;
  }

  @keyframes fade-up {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes float {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-5px);
    }
  }

  /* Actor card entrance */
  .actor-card {
    animation: fade-up 0.45s ease both;
    animation-delay: calc(var(--i, 0) * 50ms);
  }
</style>
